/**
 * An album, as a photo gallery.
 *
 * react-photo-album does the justified-rows layout, which needs each photo's
 * real aspect ratio - that is what the width/height columns the derivation
 * queue fills in are for. Without them every tile would have to be cropped
 * square, which is what the old list view did and why browsing photos was
 * unpleasant.
 */
import { useMemo, useState } from 'react';
import { RowsPhotoAlbum } from 'react-photo-album';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'react-photo-album/rows.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { Download as DownloadIcon, Images, Pencil, Share2, Trash2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { downloadUrl, rawUrl, thumbUrl } from '../api/client';
import {
  useCollection,
  useCollectionItems,
  useDeleteCollection,
  useUpdateCollection,
  useZipDownload,
} from '../api/hooks';
import { ShareDialog } from '../components/ShareDialog';
import {
  Button,
  EmptyState,
  Field,
  Input,
  Modal,
  Pager,
  Pill,
  Select,
  Spinner,
  TextArea,
  useConfirm,
} from '../components/ui';
import { thumbhashUrl } from '../lib/thumbhash';
import type { ItemSort } from '../api/types';

const SORTS: { value: ItemSort; label: string }[] = [
  { value: 'position', label: 'Album order' },
  { value: 'taken', label: 'Oldest taken first' },
  { value: 'newest', label: 'Newest taken first' },
  { value: 'name', label: 'Name' },
];

export function CollectionPage({
  collectionId,
  sort,
  page,
  onSearchChange,
}: {
  collectionId: string;
  sort: ItemSort;
  page: number;
  onSearchChange: (next: { sort?: ItemSort; page?: number }) => void;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, isLoading } = useCollection(collectionId, sort, page);
  const update = useUpdateCollection();
  const remove = useDeleteCollection();
  const items = useCollectionItems();
  const zip = useZipDownload();

  const [index, setIndex] = useState(-1);
  const [sharing, setSharing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const files = data?.items ?? [];

  /**
   * Only files with known dimensions can be laid out justified. Anything else
   * - a PDF in an album, or a photo whose derivation has not run yet - falls
   * back to a 4:3 box so it still appears rather than vanishing.
   */
  const photos = useMemo(
    () =>
      files.map((file) => ({
        key: file.id,
        src: thumbUrl(file.id, 640),
        width: file.width ?? 800,
        height: file.height ?? 600,
        alt: file.name,
        title: file.name,
        // Served as a srcSet so a phone does not download desktop-sized tiles.
        srcSet: [320, 640, 1024].map((width) => ({
          src: thumbUrl(file.id, width),
          width,
          height: Math.round(((file.height ?? 600) / (file.width ?? 800)) * width),
        })),
        blurDataURL: thumbhashUrl(file.thumbhash),
      })),
    [files],
  );

  const slides = useMemo(
    () =>
      files.map((file) =>
        file.mimeType.startsWith('video/')
          ? {
              type: 'video' as const,
              width: file.width ?? 1280,
              height: file.height ?? 720,
              poster: file.thumbnailable ? thumbUrl(file.id, 1280) : undefined,
              sources: [{ src: rawUrl(file.id), type: file.mimeType }],
              title: file.name,
              description: file.description,
              download: { url: downloadUrl(file.id), filename: file.name },
            }
          : {
              // The lightbox shows a rendered 2048px version rather than the
              // original: a 12 MP phone photo is 8 MB, and this is 300 KB.
              src: thumbUrl(file.id, 2048),
              width: file.width ?? 1600,
              height: file.height ?? 1200,
              title: file.name,
              description: file.description,
              download: { url: downloadUrl(file.id), filename: file.name },
            },
      ),
    [files],
  );

  if (isLoading) return <Spinner />;
  if (!data) return <EmptyState title="That album does not exist" />;

  const collection = data.collection;
  const canEdit = collection.role !== 'viewer';

  const startEditing = () => {
    setName(collection.name);
    setDescription(collection.description);
    setEditing(true);
  };

  const save = async () => {
    try {
      await update.mutateAsync({ id: collection.id, name, description });
      setEditing(false);
      toast.success('Saved');
    } catch (error) {
      toast.error('Could not save', { description: (error as Error).message });
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete the album “${collection.name}”?`,
      body: 'The photos themselves are not deleted — only this album and who it was shared with.',
    });
    if (!ok) return;
    await remove.mutateAsync(collection.id);
    void navigate({ to: '/collections' });
  };

  const removeCurrent = async () => {
    const file = files[index];
    if (!file) return;
    const ok = await confirm({
      title: `Remove “${file.name}” from this album?`,
      body: 'The file itself stays where it is.',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setIndex(-1);
    await items.mutateAsync({ id: collection.id, fileIds: [file.id], op: 'remove' });
  };

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-line bg-surface px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold">{collection.name}</h1>
            <p className="mt-0.5 text-sm text-ink-dim">
              {data.total} item{data.total === 1 ? '' : 's'}
              {collection.role !== 'owner' ? ` · shared by ${collection.ownerName}` : ''}
              {collection.description ? ` · ${collection.description}` : ''}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {collection.role === 'viewer' ? <Pill>View only</Pill> : null}
            <Select
              ariaLabel="Order"
              value={sort}
              onChange={(next) => onSearchChange({ sort: next, page: 1 })}
              options={SORTS}
            />
            <Button onClick={() => zip.mutate({ collectionId: collection.id })}>
              <DownloadIcon className="size-4" aria-hidden />
              <span className="hidden sm:inline">Download all</span>
            </Button>
            {canEdit ? (
              <Button onClick={startEditing}>
                <Pencil className="size-4" aria-hidden />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            ) : null}
            {collection.role === 'owner' ? (
              <>
                <Button onClick={() => setSharing(true)}>
                  <Share2 className="size-4" aria-hidden />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                <Button variant="ghost" aria-label="Delete album" onClick={onDelete}>
                  <Trash2 className="size-4 text-danger" aria-hidden />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex-1 p-3 sm:p-4">
        {files.length === 0 ? (
          <EmptyState
            icon={<Images className="size-10" />}
            title="This album is empty"
            body="Tick some files in My Files and choose “Add to album”."
          />
        ) : (
          <RowsPhotoAlbum
            photos={photos}
            targetRowHeight={(containerWidth) => (containerWidth < 640 ? 140 : 220)}
            spacing={8}
            onClick={({ index: clicked }) => setIndex(clicked)}
          />
        )}
      </div>

      <Pager
        page={data.page}
        total={data.total}
        pageSize={data.pageSize}
        onPage={(next) => onSearchChange({ page: next })}
      />

      <Lightbox
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        slides={slides}
        plugins={[Zoom, Thumbnails, Captions, Slideshow, Download, Video]}
        carousel={{ finite: true }}
        toolbar={{
          buttons: [
            canEdit ? (
              <button
                key="remove"
                type="button"
                className="yarl__button"
                onClick={removeCurrent}
                aria-label="Remove from album"
              >
                <Trash2 className="size-5" />
              </button>
            ) : null,
            'download',
            'close',
          ].filter(Boolean) as never,
        }}
      />

      {sharing ? (
        <ShareDialog
          open
          onOpenChange={setSharing}
          subjectType="collection"
          subjectId={collection.id}
          subjectName={collection.name}
        />
      ) : null}

      <Modal
        open={editing}
        onOpenChange={setEditing}
        title="Edit album"
        footer={
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" loading={update.isPending} onClick={save}>
              Save
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}
